'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { usePointerDrag } from '@/hooks/usePointerDrag';

/**
 * The one floating window in the COP.
 *
 * The drone camera window and the two dock camera windows were three copies of
 * the same shell and the same drag block — alike enough that each carried a
 * comment pointing at one of the others as the pattern it was built to. They are
 * now one implementation, so a fix to drag behaviour lands everywhere at once
 * rather than in whichever copy was in front of whoever noticed.
 *
 * Dragging and resizing are both contained by `usePointerDrag`, which holds
 * pointer capture for the duration of the gesture. The window is NOT modal: with
 * it open the operator still clicks drones, docks and the sidebar normally. Only
 * the pointer stream that starts on the header or the grip is contained, and
 * only until it is released.
 */

/** Opens where the dock cameras open — the operator drags it from there. */
const DEFAULT_POSITION = { x: 320, y: 96 };
/** Each additional window steps down-right so a second one is grabbable rather
 *  than hidden exactly beneath the first. */
const CASCADE_STEP = 32;
/** Keep the window off the very edge. */
const EDGE_MARGIN = 8;
/** How much of the window must stay on screen. Enough to grab the header and
 *  read the start of the title — a window dragged past the edge and released
 *  would otherwise be unreachable, with no way back short of a reload. */
const MIN_VISIBLE_X = 120;
const MIN_VISIBLE_Y = 40;

/** What the window opens at, and the margin it leaves on a viewport too narrow
 *  for it. Both carried over verbatim from the `w-[min(560px,calc(100vw-2rem))]`
 *  class this replaced, so nothing opens at a different size than it used to. */
const DEFAULT_WIDTH = 560;
const DEFAULT_WIDTH_MARGIN = 32;

/**
 * Smallest the operator can drag it to.
 *
 * 288 is not a round number picked for feel — it is the width of the narrowest
 * surface `CameraControlOverlay` was measured against (the details panel at its
 * `MIN_DOCK_SHEET_WIDTH`), and that component documents what happens below it:
 * the gimbal cross is atomic, so on a picture too short to hold it the keys
 * overflow behind the bottom band, which paints after them and is
 * `pointer-events-none` — "a covered key stays clickable while invisible".
 *
 * A resize floor under 288 would put a live gimbal control in that state on
 * demand, on an aircraft that may be on final approach. So the floor is the
 * smallest picture the overlay has actually been measured at, and this window
 * cannot be shrunk into territory the rest of the COP has never rendered.
 *
 * The height floor follows from it: 288 / (16/9) is 162, plus the header.
 */
const MIN_WIDTH = 288;
const MIN_HEIGHT = 200;

/** Keyboard resize step, and the step with Shift held. The grip is a real
 *  button, so a keyboard operator resizes with the arrow keys — WCAG 2.2 SC
 *  2.5.7 forbids making this drag-only. */
const RESIZE_STEP = 16;
const RESIZE_STEP_COARSE = 64;

/** A press starting on any of these is the operator using the control, not
 *  grabbing the window. */
const INTERACTIVE = 'button, a, input, select, textarea, [role="button"]';

export interface FloatingWindowPosition {
  x: number;
  y: number;
}

export interface FloatingWindowSize {
  width: number;
  height: number;
}

export interface FloatingWindowProps {
  /** Header text. Monospace uppercase is applied here — pass the raw label. */
  title: ReactNode;
  /** Rendered left of the title. Carries its own status colour. */
  icon?: ReactNode;
  /** Where it opens when uncontrolled. */
  initialPosition?: FloatingWindowPosition;
  /** Position in the open-window stack, for the cascade offset. */
  cascadeIndex?: number;
  /** Lift position into the parent when it must outlive the window. A window
   *  closed and reopened would otherwise forget where the operator put it. */
  position?: FloatingWindowPosition;
  onPositionChange?: (position: FloatingWindowPosition) => void;
  /** Lift size the same way, and for the same reason. Undefined until the parent
   *  has one to give: the opening size is measured here (see `bodyAspect`), not
   *  chosen by the caller. */
  size?: FloatingWindowSize;
  onSizeChange?: (size: FloatingWindowSize) => void;
  /**
   * Width-to-height ratio of the CONTENT, used once to derive the opening
   * height from the opening width. 16/9 for a single camera picture; a caller
   * that lays two of them side by side passes 32/9, or it would open at half
   * the height it had before this window could be resized at all.
   *
   * It constrains nothing after mount — the operator resizes freely from there,
   * and a body whose own aspect no longer matches the window letterboxes rather
   * than cropping (`object-contain`).
   */
  bodyAspect?: number;
  onClose: () => void;
  /** aria-label for the close button — windows of the same kind are otherwise
   *  indistinguishable to a screen reader. */
  closeLabel: string;
  /** aria-label for the resize grip, for the same reason. */
  resizeLabel?: string;
  /**
   * Bring this window to the front. Called on any press inside it.
   *
   * Every window is `z-50` and portaled to `<body>`, so what is on top is
   * decided by DOM order alone, and a parent that keeps its windows in a list
   * paints them in list order. Since these became resizable a window may be
   * dragged to nearly the full viewport, which can bury another one COMPLETELY
   * — still decoding, still holding a slot in the open set, and reachable by
   * nothing, because the only control that names it is its own X and that is
   * underneath. Raising on press is the way back.
   *
   * Optional: a lone window has nothing to be raised above.
   */
  onRaise?: () => void;
  /** Extra controls in the header, rendered left of the close button.
   *
   *  For verbs that act on the WINDOW rather than on its contents — the video
   *  window's pin and its "return to the stage" being the first. Close is not
   *  one of them and stays owned by this component, so every window in the COP
   *  dismisses the same way and in the same place.
   *
   *  No drag wiring needed: `handleHeaderPointerDown` already treats a press
   *  landing on a button, link or input as the operator using the control
   *  rather than grabbing the window. */
  headerActions?: ReactNode;
  /** Class overrides for the window box. The width here is only what the window
   *  is laid out at for the single layout pass before its size is measured;
   *  after that an inline width/height wins. */
  className?: string;
  children: ReactNode;
}

function clampToViewport({ x, y }: FloatingWindowPosition): FloatingWindowPosition {
  // Guard the SSR pass and jsdom, where innerWidth/innerHeight can be 0.
  const width = typeof window === 'undefined' ? 0 : window.innerWidth;
  const height = typeof window === 'undefined' ? 0 : window.innerHeight;
  const maxX = Math.max(EDGE_MARGIN, width - MIN_VISIBLE_X);
  const maxY = Math.max(EDGE_MARGIN, height - MIN_VISIBLE_Y);
  return {
    x: Math.min(Math.max(EDGE_MARGIN, x), maxX),
    y: Math.min(Math.max(EDGE_MARGIN, y), maxY),
  };
}

/**
 * Cap a size to the viewport. No floor — see `clampWindowSize`.
 *
 * Bounded by the VIEWPORT, not by the space remaining to the right of the
 * window's current position — the same choice the position clamp already makes,
 * where a window may be dragged mostly off the edge so long as a grabbable strip
 * of it remains. Coupling the two would mean a window parked near the right edge
 * could not be enlarged at all without first being dragged left.
 */
function maxSize(): FloatingWindowSize {
  // Guard the SSR pass and jsdom, where innerWidth/innerHeight can be 0.
  const viewportWidth = typeof window === 'undefined' ? 0 : window.innerWidth;
  const viewportHeight = typeof window === 'undefined' ? 0 : window.innerHeight;
  return {
    width: Math.max(EDGE_MARGIN, viewportWidth - 2 * EDGE_MARGIN),
    height: Math.max(EDGE_MARGIN, viewportHeight - 2 * EDGE_MARGIN),
  };
}

function capToViewport({ width, height }: FloatingWindowSize): FloatingWindowSize {
  const max = maxSize();
  return {
    width: Math.round(Math.min(max.width, width)),
    height: Math.round(Math.min(max.height, height)),
  };
}

/**
 * Clamp a candidate size to the bounds the grip enforces: the viewport cap
 * above, and the `MIN_WIDTH`/`MIN_HEIGHT` floor.
 *
 * The floor applies to what the OPERATOR drags to, and deliberately not to the
 * size a window opens at. A body wider than it is tall — the dock camera pair,
 * two 16:9 tiles side by side — needs less height than the floor at a narrow
 * viewport, and forcing the floor on it would open the window with a band of
 * dead black under the picture on every phone-width screen. Nothing is unsafe
 * about a window that opens shorter than the floor; the floor exists to stop the
 * operator dragging a live gimbal control into a size it was never measured at.
 */
export function clampWindowSize(size: FloatingWindowSize): FloatingWindowSize {
  const max = maxSize();
  return {
    width: Math.round(Math.min(max.width, Math.max(MIN_WIDTH, size.width))),
    height: Math.round(Math.min(max.height, Math.max(MIN_HEIGHT, size.height))),
  };
}

export function FloatingWindow({
  title,
  icon,
  initialPosition = DEFAULT_POSITION,
  cascadeIndex = 0,
  position: controlledPosition,
  onPositionChange,
  size: controlledSize,
  onSizeChange,
  bodyAspect = 16 / 9,
  onClose,
  closeLabel,
  resizeLabel = 'Resize window',
  onRaise,
  headerActions,
  className = 'w-[min(560px,calc(100vw-2rem))]',
  children,
}: FloatingWindowProps) {
  const [uncontrolledPosition, setUncontrolledPosition] = useState<FloatingWindowPosition>(() => ({
    x: initialPosition.x + cascadeIndex * CASCADE_STEP,
    y: initialPosition.y + cascadeIndex * CASCADE_STEP,
  }));
  const position = controlledPosition ?? uncontrolledPosition;

  const setPosition = useCallback(
    (next: FloatingWindowPosition) => {
      setUncontrolledPosition(next);
      onPositionChange?.(next);
    },
    [onPositionChange],
  );

  // Null for exactly one layout pass — long enough to measure the header, which
  // is the only honest source for the opening height. See the layout effect.
  const [uncontrolledSize, setUncontrolledSize] = useState<FloatingWindowSize | null>(null);
  const size = controlledSize ?? uncontrolledSize;

  const setSize = useCallback(
    (next: FloatingWindowSize) => {
      setUncontrolledSize(next);
      onSizeChange?.(next);
    },
    [onSizeChange],
  );

  // `fixed` is only relative to the viewport when no ancestor has established a
  // containing block, and `backdrop-filter` does exactly that — the dock sheet
  // (mobile) and the Fleet → Docks drawer both carry `backdrop-blur`. Rendered
  // in place, this window measured its coordinates from those panels instead,
  // so it opened mostly off-screen and the clamp below could not reach it. A
  // portal to <body> also lifts it out of their `overflow-hidden`, and takes it
  // out of the dock sidebar's DOM subtree — which is where the release click
  // used to land on a clickable ancestor underneath.
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Clamp the opening position too. The default assumes a desktop COP; on a
    // narrow viewport the window would otherwise open past the edge and stay
    // there until the operator happened to drag it. Done here rather than in
    // the useState initializer, which would run during SSR with no viewport and
    // produce a hydration mismatch on the style attribute.
    const clamped = clampToViewport(position);
    setUncontrolledPosition(clamped);
    // The parent is told ONLY when the clamp actually corrected something.
    //
    // Reporting the opening position unconditionally made "where the operator
    // put this window" indistinguishable from "where it happened to open", so a
    // parent that remembers positions (the COP, so a reopened window returns to
    // where it was dragged) recorded a position for every window that had never
    // been moved — and then handed it back on reopen instead of the free
    // cascade slot, landing the window exactly on top of another one.
    if (clamped.x !== position.x || clamped.y !== position.y) onPositionChange?.(clamped);
    // Mount only — the clamp on move and on resize covers everything after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headerRef = useRef<HTMLDivElement | null>(null);

  /**
   * Whether the current size came from the operator rather than from the
   * measurement below.
   *
   * The two are reported differently — see the layout effect and the viewport
   * listener — so "who chose this size" has to be a fact the component holds,
   * not something inferred from the value.
   */
  const operatorSized = useRef(false);
  /** True from the grip's pointerdown to its release. */
  const resizing = useRef(false);

  /** The size this window opens at: today's 560px width, and the height its
   *  body needs at that width on top of the measured header. */
  const measureSize = useCallback((): FloatingWindowSize => {
    const width = Math.min(DEFAULT_WIDTH, window.innerWidth - DEFAULT_WIDTH_MARGIN);
    const headerHeight = headerRef.current?.offsetHeight ?? 0;
    // Capped, not clamped: a body wider than it is tall needs less height than
    // the floor on a narrow viewport, and must not open with dead black under
    // the picture to satisfy a bound meant for operator drags.
    return capToViewport({ width, height: headerHeight + width / bodyAspect });
  }, [bodyAspect]);

  /**
   * Give the window an explicit size before its first paint.
   *
   * From here on it is ALWAYS explicitly sized, which is the point: a window
   * that is sometimes auto-height and sometimes fixed-height would force every
   * caller's body to work in both modes. One mode, so each body is a plain
   * `h-full`.
   *
   * The height is measured rather than declared. Stating it as a constant would
   * mean guessing at the header's height, which is one padding change away from
   * opening every camera window at the wrong size — and silently, since nothing
   * would fail.
   *
   * `useLayoutEffect`, so the measurement and the resulting size land in the
   * same frame the operator first sees. As a plain effect this would paint once
   * at the fallback width with a natural height, then jump.
   *
   * It sets the LOCAL size and deliberately does not call `onSizeChange`. A
   * lifted size means "the size the operator chose", and a measurement is not
   * that. Reporting it would also stick: a window first opened on a narrow
   * viewport would have that width written into its parent, and would keep it
   * for the rest of the session even back on a wide display, because the clamp
   * only ever shrinks.
   */
  useLayoutEffect(() => {
    if (!mounted) return;

    // The height this window is about to render at, whichever branch supplied
    // it, so the pull-up below covers BOTH. It used to run only for a measured
    // size, which was every window before the COP started remembering sizes
    // across a close - now it is every window that has NEVER been resized, and
    // a reopened one (the common case) skipped the correction entirely.
    let openingHeight: number;
    let openingWidth: number;

    if (size !== null) {
      // A size lifted into a parent OUTLIVES this component, and with it the
      // viewport listener below — the only thing that ever re-clamps a size.
      // So a window maximised on a 2560px console, closed, and reopened after
      // the laptop is undocked would render at 2544px on a 1280px screen, with
      // its header controls and its grip both roughly 1500px off-screen. The
      // position clamp cannot rescue that: it floors x at EDGE_MARGIN, and
      // 8 + 2544 is still off the right edge. That is the same unreachable
      // window MIN_VISIBLE_X exists to prevent, arrived at from the other side.
      //
      // A size that reaches us from a parent is one the operator chose, so it
      // is reported back rather than silently corrected locally.
      operatorSized.current = true;
      const clamped = clampWindowSize(size);
      if (clamped.width !== size.width || clamped.height !== size.height) setSize(clamped);
      openingHeight = clamped.height;
      openingWidth = clamped.width;
    } else {
      const measured = measureSize();
      setUncontrolledSize(measured);
      openingHeight = measured.height;
      openingWidth = measured.width;
    }

    // Pull the window up if its own height puts the grip below the fold. The
    // position clamp guarantees only MIN_VISIBLE_Y of the window is on screen,
    // which is calibrated to keep the HEADER grabbable — and the grip is at the
    // opposite corner. On a laptop or tablet in landscape the window would open
    // with its only resize affordance off-screen, and nothing tells an operator
    // that dragging it up first is what reveals the control they are looking for.
    const lowestTop = window.innerHeight - EDGE_MARGIN - openingHeight;
    // And LEFT, for the same reason on the other axis — but ONLY for a window
    // placing itself.
    //
    // `clampToViewport` floors x at `width - MIN_VISIBLE_X`, which guarantees
    // 120 px of the window is reachable: enough to grab the header and drag the
    // rest on. That is a rescue, not a placement. On a 390 px phone the default
    // x of 320 clamps to 270 while the window itself is 358 wide, so it opens
    // spanning 270→628 and everything but the title strip is off the right
    // edge. Tolerable for a window the operator deliberately popped out and can
    // drag; not for one that opens on every pick of an aircraft on the map,
    // where it would be the normal experience of the feature on a tablet or an
    // RC screen.
    //
    // Which is why this corrects ONLY a window sitting where this component put
    // it. Both other cases are somebody's deliberate choice of coordinate:
    // `controlledPosition` is the operator's, remembered by the COP so a
    // reopened window comes back where they dragged it, and an explicit
    // `initialPosition` is the caller's. Yanking either one left would silently
    // undo a placement — a window parked at the right edge of a wide console
    // would jump every time it reopened. For those the clamp keeps the last
    // word, exactly as before.
    //
    // `initialPosition === DEFAULT_POSITION` is an identity check on the default
    // parameter value, so it is true precisely when the caller passed nothing.
    const placedItself = controlledPosition === undefined && initialPosition === DEFAULT_POSITION;
    const rightmostLeft = window.innerWidth - EDGE_MARGIN - openingWidth;
    const next = {
      x:
        placedItself && position.x > rightmostLeft
          ? Math.max(EDGE_MARGIN, rightmostLeft)
          : position.x,
      y: position.y > lowestTop ? Math.max(EDGE_MARGIN, lowestTop) : position.y,
    };
    if (next.x !== position.x || next.y !== position.y) setPosition(clampToViewport(next));
    // Runs once, on the pass that first has a header to measure. Everything it
    // reads is deliberately not a dependency: a controlled caller handing a size
    // back must not re-trigger the measurement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  // `onDrag` reports deltas from the press point, so the geometry at the moment
  // of the press is the only thing either gesture needs to remember.
  const dragOrigin = useRef<FloatingWindowPosition>(position);

  const handleDragStart = useCallback(() => {
    dragOrigin.current = position;
  }, [position]);

  const handleDrag = useCallback(
    ({ dx, dy }: { dx: number; dy: number }) => {
      setPosition(clampToViewport({ x: dragOrigin.current.x + dx, y: dragOrigin.current.y + dy }));
    },
    [setPosition],
  );

  const { onPointerDown } = usePointerDrag({
    onDragStart: handleDragStart,
    onDrag: handleDrag,
  });

  const resizeOrigin = useRef<FloatingWindowSize | null>(null);

  const handleResizeStart = useCallback(() => {
    resizing.current = true;
    resizeOrigin.current = size;
  }, [size]);

  const handleResize = useCallback(
    ({ dx, dy }: { dx: number; dy: number }) => {
      const start = resizeOrigin.current;
      // Nothing to resize from: the press landed before the window had been
      // measured, which the layout effect makes effectively impossible. Refuse
      // rather than invent a starting size the operator did not choose.
      if (!start) return;
      operatorSized.current = true;
      setSize(clampWindowSize({ width: start.width + dx, height: start.height + dy }));
    },
    [setSize],
  );

  const handleResizeEnd = useCallback(() => {
    resizing.current = false;
  }, []);

  const { onPointerDown: onResizePointerDown } = usePointerDrag({
    cursor: 'nwse-resize',
    onDragStart: handleResizeStart,
    onDrag: handleResize,
    onDragEnd: handleResizeEnd,
  });

  /** What the operator sees announced after a keyboard resize. Set only on that
   *  path: a live region fed by the drag would read out every pixel. */
  const [announcement, setAnnouncement] = useState('');

  /** Back to the size the window opens at. The grip is a button, and a button
   *  that does nothing when activated reads as broken — this is what Enter and
   *  a double-click on it mean. It is also the only way back from a size the
   *  operator overshot, since a lifted size otherwise survives close-and-reopen
   *  and hands the same bad window back. */
  const resetSize = useCallback(() => {
    const measured = measureSize();
    operatorSized.current = false;
    setSize(measured);
    setAnnouncement(`Reset to ${measured.width} by ${measured.height}`);
  }, [measureSize, setSize]);

  /** The keyboard half of the grip. Without it the window would be resizable by
   *  pointer only, which SC 2.5.7 (Dragging Movements) does not allow. */
  const handleResizeKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        resetSize();
        return;
      }
      if (!size) return;
      const step = event.shiftKey ? RESIZE_STEP_COARSE : RESIZE_STEP;
      let dx = 0;
      let dy = 0;
      if (event.key === 'ArrowRight') dx = step;
      else if (event.key === 'ArrowLeft') dx = -step;
      else if (event.key === 'ArrowDown') dy = step;
      else if (event.key === 'ArrowUp') dy = -step;
      // SPACE is deliberately NOT handled and deliberately NOT stopped here.
      // `DroneCommandBar` listens for it on `window` and turns it into STOP
      // while manual authority is held — ahead of even a modal confirm dialog,
      // so that an operator reaching for the emergency stop always finds it.
      // Swallowing it because focus happens to be on a resize grip would be the
      // exact failure that listener was written to remove.
      else return;
      // Arrow keys scroll the page by default, and the COP behind this window is
      // a scrollable surface.
      event.preventDefault();
      const next = clampWindowSize({ width: size.width + dx, height: size.height + dy });
      operatorSized.current = true;
      setSize(next);
      setAnnouncement(`${next.width} by ${next.height}`);
    },
    [resetSize, setSize, size],
  );

  // The close button lives inside the drag handle, so its press bubbles here.
  // Without this, the few pixels of travel in an ordinary tap would reposition
  // the window AND arm the trailing-click suppressor that then eats the very
  // click meant to close it — leaving the operator no way to dismiss a window.
  const handleHeaderPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if ((event.target as Element).closest(INTERACTIVE)) return;
      onPointerDown(event);
    },
    [onPointerDown],
  );

  // Lets the resize listener read the current geometry without re-subscribing
  // on every move.
  const positionRef = useRef(position);
  const sizeRef = useRef(size);
  useEffect(() => {
    positionRef.current = position;
    sizeRef.current = size;
  });

  // A window parked near the right or bottom edge would be stranded off-screen
  // when the viewport shrinks — a laptop undocking from an external monitor is
  // the usual way an operator hits this. A window sized on the larger display is
  // stranded the same way, so both are pulled back.
  useEffect(() => {
    const onResize = () => {
      // Only when the clamp actually MOVED it, for the same reason the mount
      // effect is guarded: reporting an unchanged position would tell the parent
      // the operator placed this window, when all that happened is that they
      // resized the browser. A COP window that has never been dragged would then
      // be remembered at its cascade position, and handed that stale position
      // back on reopen instead of a free slot — landing it exactly on whichever
      // window has since taken that slot. That is the collision this window's
      // cascade exists to prevent, reachable through one undock.
      //
      // Guarded here rather than inside `setPosition`, because a drag at the
      // clamp edge legitimately reports positions that do not change.
      const nextPosition = clampToViewport(positionRef.current);
      if (nextPosition.x !== positionRef.current.x || nextPosition.y !== positionRef.current.y) {
        setPosition(nextPosition);
      }
      // A live grip drag already clamps every move against the current viewport.
      // Re-clamping here as well would leave `resizeOrigin` holding the
      // PRE-clamp size, and the next pointermove — which recomputes from that
      // frozen origin plus the pointer's total travel — would snap the window
      // straight back. Mobile browser chrome collapsing mid-drag is the ordinary
      // way to hit this, and devtools opening is the desktop one.
      if (resizing.current) return;
      const current = sizeRef.current;
      if (!current) return;
      const next = clampWindowSize(current);
      // Only an operator's size is reported upward. Reporting the measurement
      // would promote it into the parent permanently: a window opened on a
      // desktop and then narrowed once would be stuck at the narrow width for
      // the rest of the session, since widening the viewport back never grows a
      // size that only ever shrinks.
      if (operatorSized.current) setSize(next);
      else setUncontrolledSize(next);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setPosition, setSize]);

  if (!mounted) return null;

  return createPortal(
    <div
      // A labelled region, not a dialog. role="dialog" would be wrong twice
      // over: this window is deliberately non-modal, and DroneWorkspace
      // suppresses its own Escape-to-close whenever a [role="dialog"] exists in
      // the DOM, so claiming that role here would silently break Escape on the
      // details panel.
      role="region"
      aria-label={typeof title === 'string' ? title : closeLabel}
      // Capture, so a press raises the window even when it lands on a control
      // that stops propagation — the gimbal gesture surface does exactly that.
      // It only reorders; the press goes on to do whatever it was going to do.
      onPointerDownCapture={onRaise}
      className={`fixed z-50 flex flex-col overflow-hidden rounded-lg border border-subtle bg-popover/95 text-foreground shadow-2xl ${className}`}
      style={{ left: position.x, top: position.y, width: size?.width, height: size?.height }}
    >
      <div
        ref={headerRef}
        // touch-none stops the browser claiming a header drag as a page scroll
        // before pointer capture ever sees it.
        className="flex shrink-0 cursor-move touch-none items-center justify-between border-b divider px-3 py-2"
        onPointerDown={handleHeaderPointerDown}
      >
        <div className="flex items-center gap-2 text-xs uppercase tracking-wide">
          {icon}
          {title}
        </div>
        {/* gap-1.5, not gap-0.5: these are 24 px targets sitting exactly at the
            WCAG 2.2 2.5.8 minimum, and on the mobile dock drawer they are a
            thumb's width apart. Closing is the only way out of a window, so
            hitting dock-back instead must not be easy. */}
        <div className="flex items-center gap-1.5">
          {headerActions}
          <button
            type="button"
            onClick={onClose}
            // A visible focus ring, because X is the ONLY exit from a window:
            // this repo has no global focus style outside `ui/button.tsx`, so a
            // keyboard operator would otherwise be tabbing at an unmarked
            // target. Declared here rather than per window, so all three kinds
            // get it at once.
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-popover"
            aria-label={closeLabel}
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
      </div>
      {/* `min-h-0` or a body that wants to be taller than the window — a video
          element, a grid of them — would push the window's own height out
          instead of scrolling or letterboxing inside it. `overflow-auto` rather
          than hidden because not every body is a picture: the RTMP branch of
          `VideoStreamBody` renders a URL the operator is meant to select and
          copy, and clipping it at a small window size would put it out of reach
          with no way to scroll to it. */}
      <div className="min-h-0 flex-1 overflow-auto">{children}</div>
      {/* Not aria-live on the window itself: a live region fed by the drag would
          read out every pixel of travel. Only the keyboard path writes here. */}
      <span aria-live="polite" className="sr-only">
        {announcement}
      </span>
      <button
        type="button"
        aria-label={resizeLabel}
        // Arrow-key resize is invisible otherwise — there is nothing about a
        // corner grip that says a keyboard can drive it.
        title={`${resizeLabel}: drag, or arrow keys (Shift for larger steps). Enter resets.`}
        aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight Enter"
        // touch-none for the same reason the header carries it.
        onPointerDown={onResizePointerDown}
        onKeyDown={handleResizeKeyDown}
        onDoubleClick={resetSize}
        // It sits ON the picture, so it gets the same ground every other
        // over-video control in the COP has: bare `--on-media-muted-fg` is about
        // 1.6:1 against a white video frame, and that token set is only
        // validated over a scrim. An external dock camera pointed at an overcast
        // sky would otherwise hide the one affordance this whole feature is.
        //
        // 32px: above SC 2.5.8's 24px floor, since a grip must be acquired AND
        // dragged, but not the 40px the camera keys use — this one is laid over
        // the gimbal gesture surface, and every pixel of it is a pixel where a
        // slew drag becomes a resize instead. The scrim is what makes that
        // boundary visible rather than a surprise.
        className="absolute bottom-0 right-0 flex size-8 cursor-nwse-resize touch-none items-center justify-center rounded-tl border-l border-t border-white/15 bg-black/70 p-0 text-on-media-fg backdrop-blur-sm hover:bg-black/85 focus:outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
      >
        <svg
          viewBox="0 0 10 10"
          className="size-2.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          aria-hidden
        >
          <path d="M9 1 1 9M9 5 5 9" />
        </svg>
      </button>
    </div>,
    document.body,
  );
}
